import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Info, HelpCircle } from "lucide-react";
import { format } from "date-fns";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import { useSearchParams } from "react-router-dom";

interface AggregatedAmount {
  currentAmount: string;
  wtd: string;
  mtd: string;
  ytd: string;
}

interface AggregatedHours {
  currentAmount: string;
  wtd: string;
  mtd: string;
  ytd: string;
  currentRate: string | null;
}

interface AggregatedEarningsOutput {
  earningCode: string;
  aggregatedAmount: AggregatedAmount;
  aggregatedHours: AggregatedHours;
}

interface AggregatedDeductionOutput {
  deductionCode: string;
  employeeContributionAmount: AggregatedAmount;
  employerContributionAmount: AggregatedAmount;
}

interface SubjectWageThreshold {
  thresholdName: string;
  aggregatedAmount: AggregatedAmount;
}

interface AggregatedTaxOutput {
  taxCode: string;
  aggregatedAmount: AggregatedAmount;
  subjectWagesByThreshold: SubjectWageThreshold[];
}

interface AggregatedData {
  earningsOutputByEarningCode: Record<string, AggregatedEarningsOutput>;
  deductionsOutputByDeductionCode: Record<string, AggregatedDeductionOutput>;
  taxOutputByTaxCode: Record<string, AggregatedTaxOutput>;
}

interface HistoricalImport {
  id: number;
  fiscalYearStart: string;
  fiscalYearEnd: string;
  importDate: string;
  description: string;
  aggregatedData: AggregatedData;
}

interface NIThresholds {
  AtLel: string;
  LelToPt: string;
  PTToUEL: string;
  AboveUEL: string;
}

// Field Help Text
const fieldHelp = {
  earnings_ytd:
    "Total earnings paid through previous provider this tax year, before any deductions",
  income_tax_ytd:
    "Total income tax deducted through previous provider this tax year",
  ni_letter:
    "National Insurance category letter from previous payslips, typically 'A' for most employees",
  employee_ni_ytd:
    "Total employee National Insurance contributions deducted this tax year",
  employer_ni_ytd:
    "Total employer National Insurance contributions paid this tax year",
  student_loan_ytd:
    "Total employee Student Loan Repayment contributions deducted this tax year",
  pension_deduction_ytd:
    "Total employee Pension contributions paid this tax year",
  ni_thresholds: {
    AtLel: "Earnings up to the Lower Earnings Limit (LEL)",
    LelToPt: "Earnings from LEL up to Primary Threshold (PT)",
    PTToUEL: "Earnings from PT up to Upper Earnings Limit (UEL)",
    AboveUEL: "Earnings above the Upper Earnings Limit",
  },
};

// Location Hints
const p60LocationHints = {
  earnings_ytd:
    "Box 'Pay in this employment' under 'Pay and Income Tax details'",
  income_tax_ytd:
    "Box 'Tax deducted in this employment' under 'Pay and Income Tax details'",
  ni_letter:
    "Column 'NIC letter' under 'National Insurance contributions in this employment'",
  employee_ni_ytd:
    "Box 'Employee's contributions due on all earnings above the PT' under NI contributions",
  employer_ni_ytd: "Total found in employer's copy of P60 or final payslip",
  student_loan_ytd: "Box 'Other details' under 'Student Loan Deductions'",
  pension_deduction_ytd: "Not present in the P60",
  ni_thresholds: {
    AtLel:
      "Column 'Earnings up to LEL (£6,500 per year)' in NI contributions table",
    LelToPt:
      "Column 'Earnings from LEL up to and including PT' in NI contributions table",
    PTToUEL: "Column 'Earnings from PT up to UEL' in NI contributions table",
    AboveUEL: "Column 'Earnings above UEL' in NI contributions table",
  },
};

const importSchema = z.object({
  tax_year: z.string().regex(/^\d{4}$/, "Must be a valid year"),
  earnings_ytd: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places")
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
  income_tax_ytd: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places")
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
  ni_letter: z
    .string()
    .regex(/^[A-Z]$/, "Must be a single capital letter")
    .refine(
      (val) => ["A", "B", "C", "H", "J", "M", "Z"].includes(val),
      "Must be a valid NI letter",
    ),
  employee_ni_ytd: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places")
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
  employer_ni_ytd: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places")
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
  ni_thresholds: z.object({
    AtLel: z
      .string()
      .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places"),
    LelToPt: z
      .string()
      .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places"),
    PTToUEL: z
      .string()
      .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places"),
    AboveUEL: z
      .string()
      .regex(/^\d+(\.\d{2})?$/, "Must be a valid amount with 2 decimal places"),
  }),
  student_loan_ytd: z
    .string()
    .regex(/^(\d+(\.\d{2})?)?$/, "Must be a valid amount with 2 decimal places")
    .transform((val) => (val === "" ? "0.00" : val))
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
  pension_deduction_ytd: z
    .string()
    .regex(/^(\d+(\.\d{2})?)?$/, "Must be a valid amount with 2 decimal places")
    .transform((val) => (val === "" ? "0.00" : val))
    .refine((val) => parseFloat(val) >= 0, "Must be a positive number"),
});

type FormValues = z.infer<typeof importSchema>;

interface ImportInformationProps {
  employeeId: string;
}

interface ImportVersion {
  id: number;
  historicalImportId: number;
  changeEventId: number;
  recordedTime: Date;
  isActive: boolean;
}

interface ImportOutput {
  id: number;
  importVersionId: number;
  aggregatedData: Record<string, any>;
}

interface ImportVersionWithOutput {
  version: ImportVersion;
  output?: ImportOutput;
}

interface SubjectWageThreshold {
  thresholdName: string;
  aggregatedAmount: AggregatedAmount;
}

interface HistoricalImportsResponse {
  imports: HistoricalImport[];
  latestVersions: ImportVersionWithOutput[];
}

// Define interface for API request config
interface ApiRequestConfig {
  urlParams?: Record<string, any>;
  queryParams?: Record<string, any>;
  method?: string;
  body?: any;
}

export const ImportInformation: React.FC<ImportInformationProps> = ({
  employeeId,
}) => {
  const [hasPreviousProvider, setHasPreviousProvider] = useState(false);
  const [existingImports, setExistingImports] = useState<HistoricalImport[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestVersions, setLatestVersions] = useState<
    ImportVersionWithOutput[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [showThresholdNotice, setShowThresholdNotice] = useState(false);

  // Add a function to check if the tax year warrants showing the special notice
  const checkShowThresholdNotice = (taxYear: string) => {
    // Handle both "2025" format and "2025/26" format
    const year = parseInt(taxYear.split("/")[0], 10);
    return year === 2025 || year === 2026;
  };

  // Get auth and URL params for superuser functionality
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [searchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [userIdLoaded, setUserIdLoaded] = useState(false);

  // Extract userId from URL if superuser
  useEffect(() => {
    const userIdParam = searchParams.get("userId");
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
    setUserIdLoaded(true);
  }, [searchParams, isSuperUserAccount]);

  const currentYear = new Date().getFullYear();
  const generateTaxYearOptions = () => {
    // Generate a range of tax years from 2 years ago to 2 years ahead
    const years = [];
    for (let i = currentYear; i <= currentYear + 1; i++) {
      years.push({
        value: i.toString(),
        label: `${i - 1}/${i.toString().slice(-2)}`, // Format as 2023/24
      });
    }
    return years;
  };

  const taxYearOptions = generateTaxYearOptions();

  const form = useForm<FormValues>({
    resolver: zodResolver(importSchema),
    defaultValues: {
      tax_year: new Date().getFullYear().toString(),
      earnings_ytd: "",
      income_tax_ytd: "",
      ni_letter: "A",
      employee_ni_ytd: "",
      student_loan_ytd: "0.00",
      pension_deduction_ytd: "0.00",
      employer_ni_ytd: "",
      ni_thresholds: {
        AtLel: "",
        LelToPt: "",
        PTToUEL: "",
        AboveUEL: "",
      },
    },
  });

  useEffect(() => {
    if (employeeId && userIdLoaded) {
      fetchExistingImports();
    }
  }, [employeeId, selectedUserId, userIdLoaded]);

  const fetchExistingImports = async () => {
    try {
      const requestConfig: ApiRequestConfig = {
        urlParams: { id: employeeId },
      };

      // Add userId to queryParams if superuser and selectedUserId is not "current"
      if (isSuperUserAccount && selectedUserId !== "current") {
        requestConfig.queryParams = { userId: selectedUserId };
      }

      console.log(
        "Fetching imports with config:",
        JSON.stringify(requestConfig, null, 2),
      );

      const data = await ApiService.request<HistoricalImportsResponse>(
        EndpointKeys.FETCH_IMPORTS,
        requestConfig,
      );

      setLatestVersions(data.latestVersions);
      // Get the latest version with output
      if (!data.latestVersions) {
        return;
      }
      const latestVersion = data.latestVersions[0];
      if (latestVersion && latestVersion.output) {
        setHasPreviousProvider(true);
        setExistingImports([data.imports[0]]); // Set the single historical import

        // Map the version's output data to form fields
        const aggregatedData = latestVersion.output.aggregatedData;
        const taxOutput = aggregatedData.taxOutputByTaxCode;
        const earningsOutput = aggregatedData.earningsOutputByEarningCode;

        form.reset({
          tax_year: new Date(data.imports[0].fiscalYearEnd)
            .getFullYear()
            .toString(),
          earnings_ytd: earningsOutput.salary?.aggregatedAmount.ytd || "0.00",
          income_tax_ytd: taxOutput.income_tax?.aggregatedAmount.ytd || "0.00",
          ni_letter: "A",
          employee_ni_ytd:
            taxOutput.employee_nic?.aggregatedAmount.ytd || "0.00",
          employer_ni_ytd:
            taxOutput.employer_nic?.aggregatedAmount.ytd || "0.00",
          ni_thresholds: {
            AtLel:
              taxOutput.employee_nic?.subjectWagesByThreshold.find(
                (t: SubjectWageThreshold) => t.thresholdName === "AtLel",
              )?.aggregatedAmount.ytd || "0.00",
            LelToPt:
              taxOutput.employee_nic?.subjectWagesByThreshold.find(
                (t: SubjectWageThreshold) => t.thresholdName === "LelToPt",
              )?.aggregatedAmount.ytd || "0.00",
            PTToUEL:
              taxOutput.employee_nic?.subjectWagesByThreshold.find(
                (t: SubjectWageThreshold) => t.thresholdName === "PTToUEL",
              )?.aggregatedAmount.ytd || "0.00",
            AboveUEL:
              taxOutput.employee_nic?.subjectWagesByThreshold.find(
                (t: SubjectWageThreshold) => t.thresholdName === "AboveUEL",
              )?.aggregatedAmount.ytd || "0.00",
          },
          student_loan_ytd:
            data.imports[0].aggregatedData?.deductionsOutputByDeductionCode
              ?.student_loan?.employeeContributionAmount?.ytd || "0.00",
          pension_deduction_ytd:
            data.imports[0].aggregatedData?.deductionsOutputByDeductionCode
              ?.pension_relief_at_source?.employeeContributionAmount?.ytd ||
            "0.00",
        });

        // Check if we should show the threshold notice based on tax year
        setShowThresholdNotice(
          checkShowThresholdNotice(
            new Date(data.imports[0].fiscalYearEnd).getFullYear().toString(),
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching imports:", error);
      console.error(
        "Request configuration was:",
        JSON.stringify(
          {
            isSuperUser: isSuperUserAccount,
            selectedUserId,
            employeeId,
            userIdLoaded,
            url: window.location.href,
          },
          null,
          2,
        ),
      );
      setError("Failed to fetch existing imports");
    }
  };

  const handleProviderToggle = async (checked: boolean) => {
    setHasPreviousProvider(checked);
    if (!checked && existingImports.length > 0) {
      try {
        const latestImport = existingImports[existingImports.length - 1];
        const data = {
          reason: "Provider status changed to no previous provider",
          ...latestImport.aggregatedData,
          isActive: false,
        };

        // Prepare request config
        const requestConfig: ApiRequestConfig = {
          method: "POST",
          urlParams: {
            employeeId: employeeId,
            latestImportId: latestImport.id,
          },
          body:
            existingImports.length > 0
              ? {
                  ...data,
                  reason: "Updated P60 information",
                }
              : data,
        };

        // Add userId to queryParams if superuser and selectedUserId is not "current"
        if (isSuperUserAccount && selectedUserId !== "current") {
          requestConfig.queryParams = { userId: selectedUserId };
        }

        console.log(
          "Toggle provider with config:",
          JSON.stringify(requestConfig, null, 2),
        );

        await ApiService.request(
          EndpointKeys.HANDLE_IMPORT_PROVIDER_TOGGLE,
          requestConfig,
        );
        await fetchExistingImports();
      } catch (error) {
        console.error("Error deactivating import:", error);
        setError("Failed to update provider status");
      }
    }
  };

  const getValue = (value: string, name: string): string => {
    if (
      (name === "student_loan_ytd" || name === "pension_deduction_ytd") &&
      (value === undefined || value === null || value === "")
    ) {
      return "0.00";
    }
    return value as string;
  };

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Ensure student loan and pension values are properly formatted
      const formattedData = {
        ...data,
        student_loan_ytd: getValue(data.student_loan_ytd, "student_loan_ytd"),
        pension_deduction_ytd: getValue(
          data.pension_deduction_ytd,
          "pension_deduction_ytd",
        ),
      };

      // Calculate fiscal year dates based on selected tax year
      const taxYear = parseInt(data.tax_year, 10);
      const fiscalYearStart = `${taxYear - 1}-04-06`; // April 6th of previous year
      const fiscalYearEnd = `${taxYear}-04-05`; // April 5th of selected year

      const endpoint =
        existingImports.length > 0
          ? EndpointKeys.UPDATE_P60_IMPORTS
          : EndpointKeys.CREATE_P60_IMPORTS;

      const urlParams: Record<string, string> =
        existingImports.length > 0
          ? {
              employeeId: employeeId.toString(),
              importId:
                existingImports[existingImports.length - 1].id.toString(),
            }
          : { id: employeeId.toString() };

      // Prepare request config
      const requestConfig: ApiRequestConfig = {
        method: "POST",
        urlParams: urlParams,
        body:
          existingImports.length > 0
            ? {
                ...formattedData,
                // Include fiscal year dates to allow tax year changes in revisions
                fiscalYearStart: fiscalYearStart,
                fiscalYearEnd: fiscalYearEnd,
                reason: "Updated P60 information",
              }
            : {
                ...formattedData,
                // For new imports, include fiscal year dates
                fiscalYearStart: fiscalYearStart,
                fiscalYearEnd: fiscalYearEnd,
              },
      };

      // Add userId to queryParams if superuser and selectedUserId is not "current"
      if (isSuperUserAccount && selectedUserId !== "current") {
        requestConfig.queryParams = { userId: selectedUserId };
      }

      console.log(
        "Submit import with config:",
        JSON.stringify(requestConfig, null, 2),
      );

      await ApiService.request(endpoint, requestConfig);

      await fetchExistingImports();
    } catch (error) {
      console.error("Error submitting import:", error);
      setError("Failed to submit import data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormField = (
    name: keyof Omit<FormValues, "ni_thresholds">, // Exclude ni_thresholds from regular fields
    label: string,
    tooltipText: string,
    locationHint: string,
  ) => {
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field: { onChange, value, ...fieldProps } }) => (
          <FormItem>
            <div className="flex items-center space-x-2">
              <FormLabel>{label}</FormLabel>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{tooltipText}</p>
                    <p className="mt-2 text-sm text-muted">
                      Location on P60: {locationHint}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <FormControl>
              <Input
                type="text"
                value={getValue(value, name)}
                onChange={(e) => {
                  // For student loan and pension, handle empty input correctly
                  if (
                    (name === "student_loan_ytd" ||
                      name === "pension_deduction_ytd") &&
                    e.target.value === ""
                  ) {
                    onChange("0.00");
                  } else {
                    onChange(e.target.value);
                  }
                }}
                {...fieldProps}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  const renderNIThresholdField = (
    key: keyof NIThresholds,
    label: string,
    tooltipText: string,
    locationHint: string,
  ) => (
    <FormField
      control={form.control}
      name={`ni_thresholds.${key}` as const}
      render={({ field: { onChange, value, ...fieldProps } }) => (
        <FormItem>
          <div className="flex items-center space-x-2">
            <FormLabel>{label}</FormLabel>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                  <p className="mt-2 text-sm text-muted">
                    Location on P60: {locationHint}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <FormControl>
            <Input
              type="text"
              value={value as string}
              onChange={onChange}
              {...fieldProps}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderTaxYearField = () => (
    <FormField
      control={form.control}
      name="tax_year"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center space-x-2">
            <FormLabel>Tax Year Ending</FormLabel>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Select the tax year that appears on the P60 (year ending 5th
                    April)
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              setShowThresholdNotice(checkShowThresholdNotice(value));
            }}
            defaultValue={field.value}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select tax year" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {taxYearOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} (year ending)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <h3 className="text-2xl font-semibold leading-none tracking-tight">
            Previous Payroll Data
          </h3>
          <p className="text-sm text-muted-foreground">
            Import historical payroll data from previous providers
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6">
          <AlertDescription>
            <div className="space-y-2">
              <p>
                The UK tax year runs from 6th April to 5th April. For accurate
                tax calculations, we need details of any payments made through
                previous providers this tax year.
              </p>
              <p>
                You'll need your nanny's P60 or final payslip from the previous
                provider to complete this section.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        <div className="flex items-center space-x-2 mb-6">
          <Switch
            checked={hasPreviousProvider}
            onCheckedChange={handleProviderToggle}
          />
          <Label>
            Have you paid your nanny through another provider this tax year?
          </Label>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {hasPreviousProvider && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {renderTaxYearField()}

              {renderFormField(
                "earnings_ytd",
                "Total Earnings YTD",
                fieldHelp.earnings_ytd,
                p60LocationHints.earnings_ytd,
              )}

              {renderFormField(
                "income_tax_ytd",
                "Income Tax YTD",
                fieldHelp.income_tax_ytd,
                p60LocationHints.income_tax_ytd,
              )}

              {renderFormField(
                "student_loan_ytd",
                "Employee Student Loan YTD Contributions",
                fieldHelp.student_loan_ytd,
                p60LocationHints.student_loan_ytd,
              )}

              {renderFormField(
                "pension_deduction_ytd",
                "Employee Pension YTD Contributions",
                fieldHelp.pension_deduction_ytd,
                p60LocationHints.pension_deduction_ytd,
              )}

              <FormField
                control={form.control}
                name="ni_letter"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center space-x-2">
                      <FormLabel>NI Letter</FormLabel>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{fieldHelp.ni_letter}</p>
                            <p className="mt-2 text-sm text-muted">
                              Location on P60: {p60LocationHints.ni_letter}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select NI category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="A">A - Standard rate</SelectItem>
                        <SelectItem value="B">
                          B - Married women's reduced rate
                        </SelectItem>
                        <SelectItem value="C">C - Pension age</SelectItem>
                        <SelectItem value="H">
                          H - Apprentice under 25
                        </SelectItem>
                        <SelectItem value="J">J - Deferred rate</SelectItem>
                        <SelectItem value="M">M - Under 21</SelectItem>
                        <SelectItem value="Z">Z - No NICs due</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {renderFormField(
                "employee_ni_ytd",
                "Employee NI YTD",
                fieldHelp.employee_ni_ytd,
                p60LocationHints.employee_ni_ytd,
              )}

              {renderFormField(
                "employer_ni_ytd",
                "Employer NI YTD",
                fieldHelp.employer_ni_ytd,
                p60LocationHints.employer_ni_ytd,
              )}

              {showThresholdNotice && (
                <Alert className="mb-4 bg-amber-50 border-amber-200">
                  <AlertTitle>
                    Important: 2025-2026 NI Threshold Changes
                  </AlertTitle>
                  <AlertDescription>
                    <p>
                      For the 2025/26 tax year, the Primary Threshold (PT) is
                      now lower than the Lower Earnings Limit (LEL):
                    </p>
                    <ul className="list-disc pl-5 mt-2">
                      <li>PT: £417 per month or £5,000 per year</li>
                      <li>LEL: £6,500 per year</li>
                    </ul>
                    <p className="mt-2">
                      This is a change from previous tax years, where LEL was
                      always lower than PT. Please ensure you enter the
                      threshold values exactly as they appear on the P60 or P11
                      report.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-medium">NI Thresholds</h4>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          National Insurance contribution thresholds from P60
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderNIThresholdField(
                    "AtLel",
                    "Lower Earnings Limit (LEL)",
                    fieldHelp.ni_thresholds.AtLel,
                    p60LocationHints.ni_thresholds.AtLel,
                  )}

                  {renderNIThresholdField(
                    "LelToPt",
                    "Lower Earnings Limit to Primary Threshold",
                    fieldHelp.ni_thresholds.LelToPt,
                    p60LocationHints.ni_thresholds.LelToPt,
                  )}

                  {renderNIThresholdField(
                    "PTToUEL",
                    "Primary Threshold to Upper Earnings Limit",
                    fieldHelp.ni_thresholds.PTToUEL,
                    p60LocationHints.ni_thresholds.PTToUEL,
                  )}

                  {renderNIThresholdField(
                    "AboveUEL",
                    "Above Upper Earnings Limit",
                    fieldHelp.ni_thresholds.AboveUEL,
                    p60LocationHints.ni_thresholds.AboveUEL,
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  disabled={isSubmitting}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit Import"}
                </Button>
              </div>
            </form>
          </Form>
        )}
        {existingImports.length > 0 && (
          <div className="mt-8">
            <h4 className="text-lg font-semibold mb-4">Import History</h4>
            <Table>
              <TableCaption>Previous payroll data imports</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax Year</TableHead>
                  <TableHead>Import Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Earnings YTD</TableHead>
                  <TableHead>Tax YTD</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {existingImports.map((importData) => {
                  // Get all versions for this import
                  const importVersions = latestVersions.filter(
                    (v: ImportVersionWithOutput) =>
                      v.version.historicalImportId === importData.id,
                  );

                  // Return a row for each version of this import
                  return importVersions.map(
                    (version: ImportVersionWithOutput) => {
                      const outputData = version.output?.aggregatedData;

                      return (
                        <TableRow key={version.version.id}>
                          <TableCell>
                            {format(
                              new Date(importData.fiscalYearStart),
                              "yyyy",
                            )}
                            /{format(new Date(importData.fiscalYearEnd), "yy")}
                          </TableCell>
                          <TableCell>
                            {format(
                              new Date(version.version.recordedTime),
                              "PP",
                            )}
                          </TableCell>
                          <TableCell>
                            {importData.description}
                            {version.version.isActive && " (Active)"}
                          </TableCell>
                          <TableCell>
                            £
                            {outputData?.earningsOutputByEarningCode.salary
                              ?.aggregatedAmount.ytd || "0.00"}
                          </TableCell>
                          <TableCell>
                            £
                            {outputData?.taxOutputByTaxCode.income_tax
                              ?.aggregatedAmount.ytd || "0.00"}
                          </TableCell>
                        </TableRow>
                      );
                    },
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ImportInformation;
